import React, { useContext, useEffect, useMemo, useRef } from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { reaction } from "mobx";
import { observer } from "mobx-react";
import { useRouter, usePathname } from "../../hooks/useNavigation";
import pluralize from "pluralize";
import PropTypes from "prop-types";
import styled from "styled-components";

import { useAppState } from "../../../contexts/ApplicationStateProvider";
import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import breakpoints from "../../constants";
import BrowseAllVacancies from "../BrowseAllVacancies/BrowseAllVacancies";
import Button from "../Button/Button";
import Loader from "../Loader/Loader";
import NoVacancies from "./NoVacancies";
import Vacancy from "./Vacancy";
import VacancyList from "./VacancyList";

const StyledVacancyListWrapper = styled.div`
  min-height: 450px;
  max-height: 100vh;
  overflow: auto;

  @media (min-width: ${breakpoints.DESKTOP}) {
    max-height: 95vh;
  }
  scrollbar-color: ${({ $scrollBarColour }) =>
    `${$scrollBarColour || "initial"}`};
`;

const StyledWrapper = styled.div`
  @media (min-width: ${breakpoints.DESKTOP}) {
    max-height: 90vh;
    min-height: 90vh;
  }
`;

const StyledVacancyWrapper = styled.div`
  background-color: ${({ $backgroundColor }) =>
    `${$backgroundColor || "#fff"}!important`};
`;
function DefaultSearchResults(props) {
  const { isLoading, handleReset, thumbnailUrl } = props;
  const {
    additionalConfig,
    displayVancanciesCount = true,
    scrollBarColour,
  } = useContext(ComponentContext);

  const router = useRouter();
  const pathname = usePathname();

  const { filtersStore, vacancyStore } = useStores();
  const { searchResults, setSearchResults, pagination } = filtersStore;
  const { fetchSingleVacancy } = vacancyStore;

  const { isMobile } = useAppState();

  const vacancyListRef = useRef(null);

  useEffect(() => {
    return () => {
      setSearchResults([]);
    };
  }, []);

  useEffect(() => {
    if (vacancyListRef?.current?.scrollTop) {
      vacancyListRef.current.scrollTop = 0;
    }
  }, [router]);

  useEffect(
    () =>
      reaction(
        () => searchResults[0]?.id,
        (id) => {
          if (id) {
            const matchingVacancy = searchResults.find(
              (vacancy) => vacancy.id === id
            );

            fetchSingleVacancy(matchingVacancy);
          }
        },
        { fireImmediately: true }
      ),
    []
  );

  const styledVacancyWrapperClassName = classNames("col-lg-8", {
    "pt-5": additionalConfig?.disableBrowseButton,
  });

  const browseAllVacanciesContext = useMemo(() => {
    return {
      resultsPageHref: pathname,
    };
  }, [pathname]);

  if (isEmpty(searchResults)) {
    return <NoVacancies />;
  }

  return (
    <div className="row g-0">
      <StyledVacancyListWrapper
        ref={vacancyListRef}
        $scrollBarColour={scrollBarColour}
        className="col-lg-4 py-4 py-lg-5"
        id="infinite-scroll-default-search-results"
      >
        {isMobile && !additionalConfig?.disableBrowseButton && (
          <ComponentContext.Provider value={browseAllVacanciesContext}>
            <BrowseAllVacancies
              buttonClassName="w-100"
              className="mb-4"
              text="Clear filters"
            />
          </ComponentContext.Provider>
        )}
        <h2 className="mb-4">
          {isLoading ? (
            <Loader />
          ) : (
            pluralize(
              "Vacancy",
              pagination.totalResults,
              displayVancanciesCount
            )
          )}
        </h2>
        {isLoading ? <Loader minHeight="560px" /> : <VacancyList />}
      </StyledVacancyListWrapper>
      {!isMobile && (
        <StyledVacancyWrapper
          $backgroundColor={additionalConfig?.vacancy?.backgroundColor}
          className={styledVacancyWrapperClassName}
        >
          {!additionalConfig?.disableBrowseButton && (
            <div className="m-4 d-flex justify-content-end">
              <Button onClick={handleReset}>Browse all jobs</Button>
            </div>
          )}
          {isLoading ? (
            <Loader minHeight="560px" />
          ) : (
            <StyledWrapper className="overflow-auto d-flex flex-column px-4">
              <Vacancy thumbnailUrl={thumbnailUrl} />
            </StyledWrapper>
          )}
        </StyledVacancyWrapper>
      )}
    </div>
  );
}

export default observer(DefaultSearchResults);

DefaultSearchResults.propTypes = {
  handleReset: PropTypes.func,
  isLoading: PropTypes.bool,
  thumbnailUrl: PropTypes.string,
};

DefaultSearchResults.defaultProps = {
  handleReset: () => {},
  isLoading: false,
  thumbnailUrl: null,
};
